import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Lightbulb as InsightIcon,
  CheckCircle as RecommendationIcon,
  Warning as WarningIcon,
  Psychology as StrategyIcon,
  Lightbulb as GuidanceIcon,
  Notes as NotesIcon
} from '@mui/icons-material';

function ConsultationNotesDisplay({ consultation }) {
  if (!consultation) return null;

  const { consultationNotes, careNotes } = consultation;
  const hasNotes = consultationNotes?.summary || careNotes?.length > 0;

  if (!hasNotes) return null;

  const getCareNoteIcon = (type) => {
    switch (type) {
      case 'warning':
        return <WarningIcon color="error" />;
      case 'strategy':
        return <StrategyIcon color="secondary" />;
      default:
        return <GuidanceIcon color="primary" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <NotesIcon />
        Consultation Summary
      </Typography>

      {/* Consultation Notes Section */}
      {consultationNotes?.summary && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="medium">
              Consultation Notes
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              {/* Summary */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Summary
                </Typography>
                <Paper sx={{ p: 2, backgroundColor: 'background.paper' }}>
                  <Typography variant="body2">
                    {consultationNotes.summary}
                  </Typography>
                </Paper>
              </Box>

              {/* Key Insights */}
              {consultationNotes.keyInsights?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Key Insights
                  </Typography>
                  <List dense>
                    {consultationNotes.keyInsights.map((insight, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <InsightIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={insight} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Recommendations */}
              {consultationNotes.recommendations?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Recommendations
                  </Typography>
                  <List dense>
                    {consultationNotes.recommendations.map((recommendation, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <RecommendationIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary={recommendation} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {consultationNotes.savedAt && (
                <Typography variant="caption" color="text.secondary">
                  Saved on {new Date(consultationNotes.savedAt).toLocaleString()}
                </Typography>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Care Notes Section */}
      {careNotes?.length > 0 && (
        <Accordion defaultExpanded sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="medium">
              Care Notes ({careNotes.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              {careNotes.map((careNote, index) => (
                <Paper key={careNote._id || index} sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getCareNoteIcon(careNote.type)}
                      <Typography variant="subtitle2" fontWeight="medium">
                        {careNote.type.charAt(0).toUpperCase() + careNote.type.slice(1)}
                      </Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                      <Chip 
                        label={careNote.priority} 
                        size="small" 
                        color={getPriorityColor(careNote.priority)}
                      />
                      {careNote.active && (
                        <Chip label="Active" size="small" color="success" variant="outlined" />
                      )}
                    </Box>
                  </Box>
                  
                  <Typography variant="body2" paragraph>
                    {careNote.content}
                  </Typography>
                  
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {careNote.applicableToAI && (
                      <Chip label="For AI" size="small" variant="outlined" />
                    )}
                    {careNote.applicableToHuman && (
                      <Chip label="For Human" size="small" variant="outlined" />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                      Created {new Date(careNote.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
}

export default ConsultationNotesDisplay;